import { createContext, useCallback, useContext, useEffect } from 'react';
import { normalizeEventListDisplay } from '@/features/events/lib/eventListWindow.lib';
import type { PageExperience } from '@/features/experiences/types';
import { EventListDisplay } from '@/types/gql.generated';

export type EventListWindow = {
  eventListStartDate: string | null;
  eventListEndDate: string | null;
};

const EMPTY: EventListWindow = {
  eventListStartDate: null,
  eventListEndDate: null,
};

export type RememberedWindows = {
  remember: (viewId: string, window: EventListWindow) => void;
  get: (viewId: string) => EventListWindow | undefined;
};

export const RememberedEventListWindowsContext =
  createContext<RememberedWindows | null>(null);

/**
 * The window to write alongside a new mode, so leaving Date range and coming back
 * does not cost the author the dates they chose.
 *
 * Held for the editing session rather than persisted: several readers take
 * eventListStartDate/EndDate without checking the mode — useEventListFilters
 * bounds the events fetch straight off the start date — so dates left on a view
 * showing everything would quietly clip what it fetches.
 */
export const useRememberedEventListWindow = (experience: PageExperience) => {
  const remembered = useContext(RememberedEventListWindowsContext);
  const { id, eventListDisplay, eventListStartDate, eventListEndDate } =
    experience;

  useEffect(() => {
    const isCustom =
      normalizeEventListDisplay(eventListDisplay) === EventListDisplay.Custom;
    if (isCustom && (eventListStartDate || eventListEndDate)) {
      remembered?.remember(id, {
        eventListStartDate: eventListStartDate ?? null,
        eventListEndDate: eventListEndDate ?? null,
      });
    }
  }, [id, eventListDisplay, eventListStartDate, eventListEndDate, remembered]);

  // Per view, so editing several keeps each one's own window rather than only
  // whichever was touched last.
  return useCallback(
    (display: EventListDisplay): EventListWindow =>
      display === EventListDisplay.Custom
        ? (remembered?.get(id) ?? EMPTY)
        : EMPTY,
    [remembered, id]
  );
};
